import React from 'react';
import { DetailedItem } from '../../../../../../ui';
import { useEditableForm } from '../hooks/useEditableForm';
import { SettingsCard } from './SettingsCard';

interface NotificationSettingsData {
  emailNotifications: boolean;
  smsNotifications: boolean;
  assessmentReminders: boolean;
  candidateUpdates: boolean;
  systemAlerts: boolean;
  weeklyReports: boolean;
  notificationFrequency: 'immediate' | 'daily' | 'weekly';
  quietHoursStart: string;
  quietHoursEnd: string;
}

interface INotificationSettings {
  onSave?: (data: NotificationSettingsData) => Promise<void> | void;
}

export const NotificationSettings: React.FC<INotificationSettings> = ({ onSave }) => {
  const initialData: NotificationSettingsData = {
    emailNotifications: true,
    smsNotifications: false,
    assessmentReminders: true,
    candidateUpdates: true,
    systemAlerts: true,
    weeklyReports: true,
    notificationFrequency: 'daily',
    quietHoursStart: '22:00',
    quietHoursEnd: '08:00',
  };

  const { isEditing, formData, isSaving, handleEditToggle, handleSave, handleInputChange } = useEditableForm({
    initialData,
    onSave: async (data) => {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      if (onSave) {
        await onSave(data);
      }
    },
  });

  const icon = (
    <svg className='w-5 h-5 text-blue-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 17h5l-5 5v-5zm-4-6h8v8H11v-8zM9 3h6l4 4v8h-4V7H9V3zm0 0H5a2 2 0 00-2 2v12a2 2 0 002 2h4M9 3v4H5' />
    </svg>
  );

  const handleToggleChange = (field: keyof NotificationSettingsData) => {
    handleInputChange(field, !formData[field]);
  };

  const getFrequencyDisplayText = (frequency: string) => {
    switch (frequency) {
      case 'immediate': return 'Immediate';
      case 'daily': return 'Daily Digest';
      case 'weekly': return 'Weekly Summary';
      default: return frequency;
    }
  };

  return (
    <SettingsCard
        title='Notification Settings'
        description='Configure how and when you receive notifications'
        icon={icon}
        isEditing={isEditing}
        isSaving={isSaving}
        onEditToggle={handleEditToggle}
        onSave={handleSave}
    >
      { isEditing ? (
        // Edit Mode
        <>
          <div className='mb-6'>
            <h3 className='text-md font-medium text-gray-900 mb-4'>Notification Channels</h3>
            <div className='space-y-4'>
              <label className='flex items-center'>
                <input
                    type='checkbox'
                    checked={formData.emailNotifications}
                    onChange={() => handleToggleChange('emailNotifications')}
                    className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded'
                />
                <span className='ml-3 text-sm text-gray-700'>Email Notifications</span>
              </label>
              <label className='flex items-center'>
                <input
                    type='checkbox'
                    checked={formData.smsNotifications}
                    onChange={() => handleToggleChange('smsNotifications')}
                    className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded'
                />
                <span className='ml-3 text-sm text-gray-700'>SMS Notifications</span>
              </label>
            </div>
          </div>

          <div className='mb-6'>
            <h3 className='text-md font-medium text-gray-900 mb-4'>Notification Types</h3>
            <div className='space-y-4'>
              <label className='flex items-center'>
                <input
                    type='checkbox'
                    checked={formData.assessmentReminders}
                    onChange={() => handleToggleChange('assessmentReminders')}
                    className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded'
                />
                <span className='ml-3 text-sm text-gray-700'>Assessment Reminders</span>
              </label>
              <label className='flex items-center'>
                <input
                    type='checkbox'
                    checked={formData.candidateUpdates}
                    onChange={() => handleToggleChange('candidateUpdates')}
                    className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded'
                />
                <span className='ml-3 text-sm text-gray-700'>Candidate Updates</span>
              </label>
              <label className='flex items-center'>
                <input
                    type='checkbox'
                    checked={formData.systemAlerts}
                    onChange={() => handleToggleChange('systemAlerts')}
                    className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded'
                />
                <span className='ml-3 text-sm text-gray-700'>System Alerts</span>
              </label>
              <label className='flex items-center'>
                <input
                    type='checkbox'
                    checked={formData.weeklyReports}
                    onChange={() => handleToggleChange('weeklyReports')}
                    className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded'
                />
                <span className='ml-3 text-sm text-gray-700'>Weekly Reports</span>
              </label>
            </div>
          </div>

          <div className='mb-6'>
            <h3 className='text-md font-medium text-gray-900 mb-4'>Frequency Settings</h3>
            <div className='space-y-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>Notification Frequency</label>
                <select
                    value={formData.notificationFrequency}
                    onChange={(e) => handleInputChange('notificationFrequency', e.target.value)}
                    className='mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500'
                >
                  <option value='immediate'>Immediate</option>
                  <option value='daily'>Daily Digest</option>
                  <option value='weekly'>Weekly Summary</option>
                </select>
              </div>
            </div>
          </div>

          <div>
            <h3 className='text-md font-medium text-gray-900 mb-4'>Quiet Hours</h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>Start Time</label>
                <input
                    type='time'
                    value={formData.quietHoursStart}
                    onChange={(e) => handleInputChange('quietHoursStart', e.target.value)}
                    className='mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500'
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>End Time</label>
                <input
                    type='time'
                    value={formData.quietHoursEnd}
                    onChange={(e) => handleInputChange('quietHoursEnd', e.target.value)}
                    className='mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500'
                />
              </div>
            </div>
          </div>
        </>
      ) : (
        // View Mode
        <>
          <div className='mb-6'>
            <h3 className='text-md font-medium text-gray-900 mb-4'>Notification Channels</h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div className='flex items-center p-3 bg-gray-50 rounded-lg'>
                <div className={`w-3 h-3 rounded-full mr-3 ${formData.emailNotifications ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                <span className='text-sm text-gray-700'>Email Notifications</span>
                <span className={`ml-auto text-xs px-2 py-1 rounded-full ${formData.emailNotifications ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                  { formData.emailNotifications ? 'Enabled' : 'Disabled' }
                </span>
              </div>
              <div className='flex items-center p-3 bg-gray-50 rounded-lg'>
                <div className={`w-3 h-3 rounded-full mr-3 ${formData.smsNotifications ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                <span className='text-sm text-gray-700'>SMS Notifications</span>
                <span className={`ml-auto text-xs px-2 py-1 rounded-full ${formData.smsNotifications ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                  { formData.smsNotifications ? 'Enabled' : 'Disabled' }
                </span>
              </div>
            </div>
          </div>

          <div className='mb-6'>
            <h3 className='text-md font-medium text-gray-900 mb-4'>Active Notification Types</h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              { [
                { key: 'assessmentReminders', label: 'Assessment Reminders' },
                { key: 'candidateUpdates', label: 'Candidate Updates' },
                { key: 'systemAlerts', label: 'System Alerts' },
                { key: 'weeklyReports', label: 'Weekly Reports' },
              ].map(({ key, label }) => (
                <div key={key} className='flex items-center p-3 bg-gray-50 rounded-lg'>
                  <div className={`w-3 h-3 rounded-full mr-3 ${formData[key as keyof NotificationSettingsData] ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                  <span className='text-sm text-gray-700'>{ label }</span>
                  <span className={`ml-auto text-xs px-2 py-1 rounded-full ${formData[key as keyof NotificationSettingsData] ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                    { formData[key as keyof NotificationSettingsData] ? 'On' : 'Off' }
                  </span>
                </div>
              )) }
            </div>
          </div>

          <div className='mb-6'>
            <div className='p-4 bg-blue-50 rounded-lg border border-blue-200'>
              <DetailedItem
                  label='Notification Frequency'
                  text={getFrequencyDisplayText(formData.notificationFrequency)}
                  helpertext='How often you receive notification summaries'
              />
            </div>
          </div>

          <div>
            <h3 className='text-md font-medium text-gray-900 mb-4'>Quiet Hours</h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div className='p-4 bg-gray-50 rounded-lg'>
                <DetailedItem
                    label='Start Time'
                    text={formData.quietHoursStart}
                />
              </div>
              <div className='p-4 bg-gray-50 rounded-lg'>
                <DetailedItem
                    label='End Time'
                    text={formData.quietHoursEnd}
                />
              </div>
            </div>
            <p className='text-sm text-gray-500 mt-2'>No notifications will be sent during quiet hours</p>
          </div>
        </>
      ) }
    </SettingsCard>
  );
};
